// Temporary sessionStorage-backed mock for EXR admin plans until the EXR backend exists. (GMTEC-20714)
// TODO: Replace entire file with real API calls once the EXR plan endpoints exist; delete together with the other EXR mocks. (GMTEC-20714)
import { PlanType, SeasonProcessState } from '@coin/shared/core/util-enums';
import { PaginatedResult } from '@coin/shared/core/util-models';
import {
  ExceptionalRecognitionCompensationType,
  ExceptionalRecognitionPaymentCategory
} from '@coin/shared/exceptional-recognitions/util';
import { ExceptionalRecognitionPlan } from '../models/exceptional-recognition-plan.model';
import { hasExrRecords } from './exr-record-store';
import { getExrSeason, updateExrSeason } from './exr-season-store';

const PLANS_KEY = 'exr-mock-plans';

export function listExrPlans(seasonId: string, page = 0, size = 0): PaginatedResult<ExceptionalRecognitionPlan> {
  const plans = sortedPlans(ensureDefaultPlan(seasonId));
  const pageSize = size || plans.length || 1;
  const start = page * pageSize;
  return {
    content: plans.slice(start, start + pageSize),
    total: plans.length,
    currentPage: page + 1,
    pageCount: Math.max(1, Math.ceil(plans.length / pageSize)),
    pageSize,
    hasMore: start + pageSize < plans.length
  };
}

export function createExrPlan(seasonId: string, plan: Partial<ExceptionalRecognitionPlan>): ExceptionalRecognitionPlan {
  const plans = ensureDefaultPlan(seasonId);
  const generalPlans = plans.filter(existing => existing.type === PlanType.General);
  const created: ExceptionalRecognitionPlan = {
    id: `exr-mock-plan-${crypto.randomUUID()}`,
    seasonId,
    title: plan.title ?? '',
    priority: generalPlans.length ? Math.max(...generalPlans.map(existing => existing.priority)) + 1 : 0,
    type: PlanType.General,
    category: plan.category,
    // Sign-On Bonus and Transformation Incentive are locked to Stock Awards; only Exceptional Recognition is configurable.
    allowedCompensationTypes:
      plan.allowedCompensationTypes ??
      (plan.category === ExceptionalRecognitionPaymentCategory.ExceptionalRecognition
        ? []
        : [ExceptionalRecognitionCompensationType.StockAwards]),
    isExcluded: plan.isExcluded ?? false
  };
  writePlans(seasonId, [...plans, created]);

  // The first non-default plan moves the season from Population to PlanManagement. (GMTEC-20714 / GMTEC-20689)
  if (!generalPlans.length && getExrSeason(seasonId)?.state === SeasonProcessState.Population) {
    updateExrSeason({ id: seasonId, state: SeasonProcessState.PlanManagement });
  }
  return created;
}

export function updateExrPlans(seasonId: string, updated: ExceptionalRecognitionPlan[]): ExceptionalRecognitionPlan[] {
  const byId = new Map(readPlans(seasonId).map(plan => [plan.id, plan]));
  updated.forEach(plan => byId.set(plan.id, { ...byId.get(plan.id), ...plan }));
  const plans = [...byId.values()];
  writePlans(seasonId, plans);
  return plans;
}

// Returns why the plan can't be deleted, or null once it is gone. Mock records carry no plan link, so any proposal in
// the season blocks the delete. (GMTEC-20714)
export function deleteExrPlan(seasonId: string, planId: string): string | null {
  if (getExrSeason(seasonId)?.state !== SeasonProcessState.PlanManagement) {
    return 'Plans can only be deleted while the season is in Plan Management';
  }
  if (hasExrRecords(seasonId)) {
    return 'Plans can only be deleted while no proposals exist';
  }
  writePlans(
    seasonId,
    readPlans(seasonId).filter(plan => plan.id !== planId)
  );
  return null;
}

// The default excluded plan is seeded with the season: it sits at the lowest priority, carries no category and always
// uses the season's own ruleset, which the population step may only set after the plan exists. (GMTEC-20685 / GMTEC-20714)
function ensureDefaultPlan(seasonId: string): ExceptionalRecognitionPlan[] {
  const seasonRuleSetId = getExrSeason(seasonId)?.ruleSetId;
  const plans = readPlans(seasonId);
  const defaultPlan = plans.find(plan => plan.type === PlanType.Default);
  if (defaultPlan && defaultPlan.ruleSetId === seasonRuleSetId) {
    return plans;
  }
  const withDefault: ExceptionalRecognitionPlan[] = [
    ...plans.filter(plan => plan.type !== PlanType.Default),
    {
      id: `exr-mock-plan-default-${seasonId}`,
      seasonId,
      title: 'Default',
      priority: Number.MAX_SAFE_INTEGER,
      type: PlanType.Default,
      ruleSetId: seasonRuleSetId,
      isExcluded: true
    }
  ];
  writePlans(seasonId, withDefault);
  return withDefault;
}

function sortedPlans(plans: ExceptionalRecognitionPlan[]): ExceptionalRecognitionPlan[] {
  return [...plans].sort((a, b) => {
    if (a.type === PlanType.Default) return 1;
    if (b.type === PlanType.Default) return -1;
    return a.priority - b.priority;
  });
}

function readPlans(seasonId: string): ExceptionalRecognitionPlan[] {
  return readStore()[seasonId] ?? [];
}

function writePlans(seasonId: string, plans: ExceptionalRecognitionPlan[]): void {
  const store = readStore();
  store[seasonId] = plans;
  sessionStorage.setItem(PLANS_KEY, JSON.stringify(store));
}

function readStore(): Record<string, ExceptionalRecognitionPlan[]> {
  try {
    return JSON.parse(sessionStorage.getItem(PLANS_KEY) ?? '{}');
  } catch {
    return {};
  }
}
